import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { customBaseUrl, httpGet } from '../../store/http';
import { NotificationManager } from 'react-notifications';
import {
    Input,
    Button,
    Form,
    Row,
    Col,
    Card,
    Typography,
    Space,
    Select,
    Tag,
    DatePicker,
    Divider,
    Modal,
    Descriptions,
} from 'antd';
import { DataGrid } from '@mui/x-data-grid';
import { Box } from '@mui/material';
import AdminLayout from "../../components/Layout";
import LoanDashboardLayout from '../../components/Layout/LoanDashboardLayout';
import { showLoader, hideLoader } from '../../utils/loader';
import slugs from '../../resources/slugs';
import { useMatStyles, CardWrapper } from './style';
import { SearchOutlined, HistoryOutlined, EyeOutlined } from '@ant-design/icons';
import moment from 'moment';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const LoanAuditLogsPage = ({ from }) => {
    const classes = useMatStyles();
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ page: 0, limit: 10 });
    const [totalRows, setTotalRows] = useState(0);

    const [selectedLog, setSelectedLog] = useState(null);
    const [isViewMoreModalVisible, setIsViewMoreModalVisible] = useState(false);

    const [filters, setFilters] = useState({
        actorId: '',
        action: '',
        entityType: '',
        entityId: '',
        startDate: '',
        endDate: '',
    });

    const fetchAuditLogs = async (currentFilters, currentPage, currentPageSize) => {
        setLoading(true);
        showLoader();
        try {
            const token = from === 'admin' ? localStorage.getItem('token') : localStorage.getItem('loanAdminToken');
            if (!token) {
                window.location.href = '/loan-admin-login';
                return;
            }

            const queryParams = new URLSearchParams({
                page: currentPage + 1,
                limit: currentPageSize,
            });

            if (currentFilters.actorId) queryParams.append('actorId', currentFilters.actorId);
            if (currentFilters.action) queryParams.append('action', currentFilters.action);
            if (currentFilters.entityType) queryParams.append('entityType', currentFilters.entityType);
            if (currentFilters.entityId) queryParams.append('entityId', currentFilters.entityId);
            if (currentFilters.startDate) queryParams.append('startDate', currentFilters.startDate);
            if (currentFilters.endDate) queryParams.append('endDate', currentFilters.endDate);

            const res = await httpGet(
                `${customBaseUrl.loanUrl}/api/v1/admin/audit-logs?${queryParams.toString()}`,
                null, false, false, token
            );

            if (res.status && res.data) {
                setLogs(res.data || []);
                setTotalRows(res.pagination?.total || res.data.length || 0);
            } else {
                setLogs([]);
                setTotalRows(0);
                if (res.message && res.message !== "Internal Server Error") {
                    NotificationManager.info(res.message);
                }
            }
        } catch (error) {
            console.error('Error fetching audit logs:', error);
            NotificationManager.error('Error fetching audit logs');
        } finally {
            setLoading(false);
            hideLoader();
        }
    };

    const location = useLocation();

    useEffect(() => {
        if (location.state) {
            const { actorId, entityId } = location.state;
            const newFilters = {
                ...filters,
                actorId: actorId || filters.actorId,
                entityId: entityId || filters.entityId,
            };
            setFilters(newFilters);
            fetchAuditLogs(newFilters, 0, pagination.limit);
        } else {
            fetchAuditLogs(filters, pagination.page, pagination.limit);
        }
    }, [pagination.page, pagination.limit, location.state]);

    const handleFilterChange = (name, value) => {
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleDateChange = (dates) => {
        if (dates) {
            setFilters(prev => ({
                ...prev,
                startDate: dates[0].format('YYYY-MM-DD'),
                endDate: dates[1].format('YYYY-MM-DD'),
            }));
        } else {
            setFilters(prev => ({ ...prev, startDate: '', endDate: '' }));
        }
    };

    const handleSearch = () => {
        setPagination(prev => ({ ...prev, page: 0 }));
        fetchAuditLogs(filters, 0, pagination.limit);
    };

    const handleClear = () => {
        setFilters({
            actorId: '',
            action: '',
            entityType: '',
            entityId: '',
            startDate: '',
            endDate: '',
        });
        setPagination({ page: 0, limit: 10 });
        fetchAuditLogs({ actorId: '', action: '', entityType: '', entityId: '', startDate: '', endDate: '' }, 0, 10);
    };

    const columns = [
        {
            field: 'actor_name',
            headerName: 'Admin Name',
            width: 180,
            renderCell: (params) => <Text strong style={{ fontSize: 13 }}>{params.row.actor_name || 'N/A'}</Text>
        },
        {
            field: 'actor_id',
            headerName: 'Admin ID',
            width: 250,
            renderCell: (params) => (
                <Space direction="vertical" size={0}>
                    <Text strong style={{ fontSize: 13 }}>{params.row.metadata?.email || ''}</Text>
                    <Text type="secondary" style={{ fontSize: 11 }} copyable={{ text: params.value }}>{params.value}</Text>
                </Space>
            )
        },
        {
            field: 'action',
            headerName: 'Action',
            width: 150,
            renderCell: (params) => {
                let color = 'default';
                const action = params.value?.toLowerCase() || '';
                if (action.includes('approve')) color = 'green';
                else if (action.includes('reject') || action.includes('decline')) color = 'red';
                else if (action.includes('login')) color = 'blue';
                else if (action.includes('disburse')) color = 'gold';
                else if (action.includes('update') || action.includes('edit')) color = 'cyan';
                return <Tag color={color} style={{ borderRadius: 6, fontWeight: 600, textTransform: 'uppercase', fontSize: 10 }}>{params.value}</Tag>;
            }
        },
        {
            field: 'entity_type',
            headerName: 'Entity',
            width: 120,
            renderCell: (params) => params.value ? <Tag style={{ borderRadius: 4 }}>{params.value.toUpperCase()}</Tag> : <Text type="secondary">-</Text>
        },
        {
            field: 'entity_id',
            headerName: 'Loan ID',
            width: 150,
            renderCell: (params) => <Text copyable={{ text: params.value }} style={{ fontSize: 12 }}>{params.value ? `${params.value.substring(0, 8)}...` : '-'}</Text>
        },
        {
            field: 'metadata',
            headerName: 'Details',
            width: 250,
            renderCell: (params) => {
                if (!params.value || Object.keys(params.value).length === 0) return <Text type="secondary">-</Text>;
                return (
                    <Text ellipsis={{ tooltip: JSON.stringify(params.value, null, 2) }}>
                        {Object.entries(params.value)
                            .filter(([key]) => key !== 'actor_name' && key !== 'email')
                            .map(([key, val]) => `${key}: ${typeof val === 'object' ? '{...}' : val}`)
                            .join(', ')}
                    </Text>
                );
            }
        },
        {
            field: 'timestamp',
            headerName: 'Occurrence',
            width: 200,
            renderCell: (params) => (
                <Space direction="vertical" size={0}>
                    <Text style={{ fontSize: 13 }}>{moment(params.value).format('MMM DD, YYYY')}</Text>
                    <Text type="secondary" style={{ fontSize: 11 }}>{moment(params.value).format('hh:mm A')}</Text>
                </Space>
            )
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 120,
            renderCell: (params) => (
                <Button
                    icon={<EyeOutlined />}
                    onClick={() => { setSelectedLog(params.row); setIsViewMoreModalVisible(true); }}
                    size="small"
                >
                    View More
                </Button>
            )
        }
    ];

    const Layout = from === "admin" ? AdminLayout : LoanDashboardLayout;

    return (
        <Layout>
            <div style={{ padding: '0 32px 32px 32px' }}>
                <div style={{ marginBottom: 32 }}>
                    <Title level={2} style={{ margin: 0, fontWeight: 800 }}>Loan Audit Logs</Title>
                    <Text type="secondary">Review system activities and administrative actions across the loan module.</Text>
                </div>

                <CardWrapper>
                    <Card style={{ marginBottom: 24, borderRadius: 16 }}>
                        <Form layout="vertical">
                            <Row gutter={[24, 16]}>
                                <Col xs={24} md={8} lg={6}>
                                    <Form.Item label="Admin ID">
                                        <Input
                                            placeholder="Enter admin or ID"
                                            value={filters.actorId}
                                            onChange={(e) => handleFilterChange('actorId', e.target.value)}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={8} lg={6}>
                                    <Form.Item label="Loan ID">
                                        <Input
                                            placeholder="Enter loan  ID"
                                            value={filters.entityId}
                                            onChange={(e) => handleFilterChange('entityId', e.target.value)}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={8} lg={6}>
                                    <Form.Item label="Action">
                                        <Select
                                            placeholder="Select action"
                                            value={filters.action}
                                            onChange={(val) => handleFilterChange('action', val)}
                                            allowClear
                                        >
                                            <Option value="Approve">Approve</Option>
                                            <Option value="Reject">Reject</Option>
                                            <Option value="Login">Login</Option>
                                            <Option value="Create">Create</Option>
                                            <Option value="Disburse">Disburse</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                                {/* <Col xs={24} md={4}>
                                    <Form.Item label={<Text strong style={{ fontSize: 12 }}>ENTITY TYPE</Text>}>
                                        <Select
                                            placeholder="Select type"
                                            value={filters.entityType}
                                            onChange={(val) => handleFilterChange('entityType', val)}
                                            allowClear
                                        >
                                            <Option value="LOAN">Loan</Option>
                                            <Option value="USER">User</Option>
                                            <Option value="ROLE">Role</Option>
                                        </Select>
                                    </Form.Item>
                                </Col> */}
                                <Col xs={24} md={7}>
                                    <Form.Item label={<Text strong style={{ fontSize: 12 }}>DATE RANGE</Text>}>
                                        <RangePicker
                                            style={{ width: '100%' }}
                                            onChange={handleDateChange}
                                            value={filters.startDate ? [moment(filters.startDate), moment(filters.endDate)] : null}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={4} style={{ display: 'flex', alignItems: 'flex-end' }}>
                                    <Space style={{ marginBottom: 24, width: '100%' }}>
                                        <Button onClick={handleClear} block>Clear</Button>
                                        <Button
                                            type="primary"
                                            icon={<SearchOutlined />}
                                            onClick={handleSearch}
                                            style={{ background: '#FF4B01', border: 'none' }}
                                            block
                                        >
                                            Search
                                        </Button>
                                    </Space>
                                </Col>
                            </Row>
                        </Form>
                    </Card>
                </CardWrapper>

                <Box sx={{
                    height: 650,
                    width: '100%',
                    '& .MuiDataGrid-root': {
                        border: 'none',
                        background: '#fff',
                        borderRadius: '16px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                        '& .MuiDataGrid-columnHeaders': {
                            backgroundColor: '#fafafa',
                            borderBottom: '1px solid #f0f0f0'
                        },
                        '& .MuiDataGrid-cell:focus': { outline: 'none' }
                    }
                }}>
                    <DataGrid
                        rows={logs}
                        columns={columns}
                        loading={loading}
                        pagination
                        paginationMode="server"
                        rowCount={totalRows}
                        pageSize={pagination.limit}
                        page={pagination.page}
                        onPageChange={(newPage) => setPagination(prev => ({ ...prev, page: newPage }))}
                        onPageSizeChange={(newPageSize) => setPagination({ page: 0, limit: newPageSize })}
                        rowsPerPageOptions={[10, 20, 50]}
                        disableSelectionOnClick
                    />
                </Box>

                <Modal
                    title={<Title level={4} style={{ margin: 0 }}>Log Details</Title>}
                    visible={isViewMoreModalVisible}
                    onCancel={() => setIsViewMoreModalVisible(false)}
                    footer={[
                        <Button key="close" onClick={() => setIsViewMoreModalVisible(false)}>Close</Button>
                    ]}
                    width={700}
                >
                    {selectedLog && (
                        <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                            <Descriptions bordered column={1} size="small">
                                <Descriptions.Item label="Action">{selectedLog.action}</Descriptions.Item>
                                <Descriptions.Item label="Admin ID">{selectedLog.actor_id}</Descriptions.Item>
                                <Descriptions.Item label="Admin Email">{selectedLog.metadata?.email || 'N/A'}</Descriptions.Item>
                                <Descriptions.Item label="Admin Name">{selectedLog.actor_name || 'N/A'}</Descriptions.Item>
                                <Descriptions.Item label="Entity Type">{selectedLog.entity_type || 'N/A'}</Descriptions.Item>
                                <Descriptions.Item label="Loan ID">{selectedLog.entity_id || 'N/A'}</Descriptions.Item>
                                <Descriptions.Item label="Timestamp">{moment(selectedLog.timestamp).format('LLL')}</Descriptions.Item>
                            </Descriptions>
                            <Divider orientation="left">Metadata / Full Details</Divider>
                            <pre style={{ background: '#f5f5f5', padding: 16, borderRadius: 8, fontSize: 12 }}>
                                {JSON.stringify(selectedLog.metadata || {}, null, 2)}
                            </pre>
                        </div>
                    )}
                </Modal>
            </div>
        </Layout>
    );
};

export default LoanAuditLogsPage;
